<template>
  <div class="whitebackground">
    <Stack>
      <!-- Loading Spinner -->
      <div
        v-if="holdingsloading"
        class="tw-flex tw-justify-center tw-items-center tw-h-screen"
      >
        <i class="fa fa-spinner fa-spin fa-3x tw-text-gray-500" />
      </div>

      <!-- Magic Ticker Fill Component -->
      <MagicTickerFill
        :coverage-warnings="coverageWarnings || []"
        :number-unmatched-holdings="numberUnmatchedHoldings || 0"
        @holdings-updated="onHoldingsUpdated"
        @mtf-files-updated="onMTFFilesUpdated"
      />

      <!-- Portfolio Total Box -->
      <Level>
        <LevelLeft>
          <Title> Portfolio Summary </Title>
          <div
            v-if="hasAggregatedPositions"
            class="tw-flex tw-items-center tw-gap-2 tw-mt-2"
          >
            <Subtitle>Show all tax lots</Subtitle>
            <Toggle
              :model-value="showAllTaxLots"
              @update:modelValue="value => showAllTaxLots = value"
            />
          </div>
        </LevelLeft>

        <LevelRight>
          <Buttons v-if="can_edit">
            <Button
              variant="danger"
              icon-before="fas fa-trash"
              :disabled="!selectedHoldingIds.length"
              @click="showBulkDeleteConfirmation = true"
            >
              Delete Selected
            </Button>
            <Button
              v-if="showReplaceButton"
              variant="primary"
              icon-before="fas fa-exchange-alt"
              @click="$emit('replace-portfolio')"
            >
              Replace Portfolio
            </Button>
            <Button
              v-if="showAddButton"
              variant="primary"
              icon-before="fas fa-plus-square"
              @click="$emit('add-portfolio')"
            >
              Add Portfolio
            </Button>
            <Button
              v-if="!inCheckup"
              variant="primary"
              icon-before="fas fa-upload"
              @click="showCSVModal = true"
            >
              Upload More Holdings
            </Button>
            <Button
              v-if="showDownloadButton"
              variant="primary"
              icon-before="fas fa-download"
              :disabled="downloadDisabled"
              @click="$emit('download')"
            >
              {{ downloadDisabled ? 'Preparing…' : 'Download Holdings' }}
            </Button>
            <Button
              variant="primary"
              icon-before="fas fa-plus"
              @click="addNewHolding"
            >
              Add Holding
            </Button>
          </Buttons>
        </LevelRight>
      </Level>

      <div class="summary">
        <PortfolioSummaryTree
          :node="summaryTree"
          :depth="0"
        />
      </div>

      <div
        v-if="hasSyncedPortfolio"
        class="tw-bg-yellow-100 tw-text-yellow-800 tw-p-3 tw-mb-4 tw-rounded tw-border tw-border-yellow-300"
      >
        <i class="fas fa-exclamation-triangle tw-mr-2" />
        Warning: Some positions are held by synced clients. Edits to these
        holdings will be overwritten during the nightly custodian sync.
      </div>

      <!-- Holdings Table -->
      <div class="table-container">
        <o-table
          :key="tableRenderKey"
          :data="tableData"
          sortable
          hoverable
          class="holdings-table"
          :row-class="getRowClass"
        >
          <o-table-column
            field="selection"
            label=""
            width="48"
          >
            <template #header>
              <div class="tw-flex tw-justify-center">
                <input
                  type="checkbox"
                  class="tw-form-checkbox tw-h-4 tw-w-4 tw-text-blue-600"
                  :disabled="!selectableRows.length"
                  :checked="allSelectableRowsSelected"
                  :indeterminate.prop="someSelectableRowsSelected"
                  @change="toggleSelectAllRows"
                >
              </div>
            </template>
            <template #default="props">
              <div class="tw-flex tw-justify-center">
                <input
                  type="checkbox"
                  class="tw-form-checkbox tw-h-4 tw-w-4 tw-text-blue-600"
                  :disabled="!isRowSelectable(props.row)"
                  :checked="isRowSelected(props.row)"
                  @change="toggleRowSelection(props.row, $event)"
                >
              </div>
            </template>
          </o-table-column>
          <o-table-column
            v-slot="props"
            field="activeSymbol"
            label="Symbol/CUSIP"
            width="200"
            sortable
          >
            <div
              class="name-column"
              :class="{ 'child-tax-lot': props.row.isChildTaxLot }"
            >
              <Stack
                v-if="editingRow === props.row.id"
                gap="2"
              >
                <!-- Integrated ticker input using SearchTickers: -->
                <SearchTickers
                  ref="tickerSearch"
                  :init-value="useSymbol(editedRow)"
                  @select="handleTickerSelect"
                  @matched="handleTickerMatched"
                />
                <p class="required tw-text-red-600 tw-font-bold tw-mt-1">
                  Required
                </p>

                <Buttons
                  no-wrap
                  size="3"
                >
                  <Button
                    :disabled="isSaving"
                    variant="success"
                    tabindex="-1"
                    @click="saveRow(props.row)"
                  >
                    <i
                      v-if="isSaving"
                      class="fas fa-spinner fa-spin tw-mr-2"
                    />
                    <span
                      v-else
                      class="fas fa-save tw-mr-1"
                    />
                    {{ isSaving ? "Saving…" : "Save" }}
                  </Button>
                  <Button
                    title="Cancel"
                    tabindex="-1"
                    variant="warning"
                    @click="cancelEdit()"
                  >
                    Cancel
                  </Button>
                </Buttons>
              </Stack>

              <Stack
                v-else
                gap="0"
              >
                <div class="tw-flex tw-items-center tw-gap-2">
                  <!-- Expand/Collapse button for aggregated positions -->
                  <Button
                    v-if="props.row.isAggregated && props.row.taxLots.length > 1"
                    size="2"
                    variant="ghost"
                    :icon-only="expandedPositions[props.row.id] ? 'fas fa-chevron-down' : 'fas fa-chevron-right'"
                    class="tw-mr-1"
                    @click="togglePositionExpansion(props.row.id)"
                  />

                  <div class="tw-flex-1">
                    <Title
                      size="4"
                      class="tw-break-all"
                    >
                      {{ props.row.activeSymbol }}
                      <span
                        v-if="props.row.isAggregated && props.row.taxLots.length > 1"
                        class="tw-text-sm tw-text-gray-500"
                      >
                        ({{ props.row.taxLots.length }} lots)
                      </span>
                    </Title>
                    <p class="tw-break-all stk-color-text-70 stk-size-3">
                      {{ props.row.uploadedSymbol || '' }}
                    </p>
                    <p
                      v-if="!props.row.matched"
                      class="tw-text-red-500"
                    >
                      Not Matched
                    </p>
                  </div>
                </div>

                <Buttons
                  v-if="can_edit && editingRow !== props.row.id"
                  class="action-buttons"
                  no-wrap
                  size="3"
                >
                  <Button
                    :title="(props.row.isAggregated || props.row.isChildTaxLot)
                      ? 'Switch to individual tax lots and edit'
                      : 'Edit this holding'"
                    icon-before="fas fa-edit"
                    @click="startEdit(props.row)"
                  >
                    {{ (props.row.isAggregated || props.row.isChildTaxLot) ? 'Enter Edit Mode' : 'Edit' }}
                  </Button>
                  <Button
                    v-if="!props.row.isAggregated && !props.row.isChildTaxLot"
                    title="Delete this holding"
                    icon-only="fas fa-trash"
                    variant="danger"
                    @click="confirmDelete(props.row)"
                  />
                </Buttons>
              </Stack>
            </div>
          </o-table-column>

          <!-- Name -->
          <o-table-column
            v-slot="props"
            field="name"
            label="Name"
            sortable
          >
            <div class="tw-relative">
              <template v-if="editingRow === props.row.id">
                <!-- Only show the name input if this is not a new holding or if a ticker has been selected -->
                <template v-if="!props.row.isTemporary || editedRow.activeSymbol">
                  <input
                    v-model="editedRow.name"
                    class="tw-w-full tw-px-2 tw-py-1 tw-border tw-border-gray-300 disabled-input"
                    disabled
                  >
                </template>
              <!-- If it's a new holding and no ticker is selected, nothing shows up here -->
              </template>
              <template v-else>
                <span>{{ props.row.name }}</span>
              </template>
            </div>
          </o-table-column>

          <!-- Market Value -->
          <o-table-column
            v-slot="props"
            field="marketValue"
            label="Market Value"
            sortable
            position="right"
            class="tw-text-left"
            :width="130"
          >
            <div class="tw-relative tw-text-left">
              <template v-if="editingRow === props.row.id">
                <!-- Check if we're in "new holding" mode: -->
                <template v-if="editingRow === props.row.id">
                  <input
                    ref="marketValueInput"
                    v-model="editedRow.marketValue"
                    class="tw-w-full tw-px-2 tw-py-1 tw-border tw-border-gray-300"
                    type="number"
                    placeholder="Market Value"
                    step="0.01"
                    :class="{'flash-green': marketValueFlash}"
                    @keydown="handleEditRowKeyDown(props.row, $event)"
                  >
                  <p class="required tw-text-red-600 tw-font-bold tw-mt-1">
                    Required
                  </p>
                  <div
                    v-if="validationErrors.marketValue"
                    class="tw-text-red-500 tw-text-xs tw-mt-1"
                  >
                    {{ validationErrors.marketValue }}
                  </div>
                  <div
                    v-if="validationErrors.activeSymbol"
                    class="tw-text-red-500 tw-text-xs tw-mt-1"
                  >
                    {{ validationErrors.activeSymbol }}
                  </div>
                </template>
              </template>

              <!-- Display mode (non-editing) -->
              <template v-else>
                <strong class="stk-size-3_5">{{ toCurrency(props.row.marketValue) }}</strong>
              </template>
            </div>
          </o-table-column>


          <!-- Cost basis Purchase Date -->
          <o-table-column
            v-slot="props"
            field="purchaseDate"
            label="Cost Basis and Purchase Date"
            :width="120"
            sortable
          >
            <div
              class="tw-relative"
              :style="editingRow === props.row.id && editedRow.activeSymbol ? { borderLeft: '3px solid #f2f2f2', paddingLeft: '5px' } : {}"
            >
              <template v-if="editingRow === props.row.id && !props.row.isAggregated">
                <Stack
                  gap="2"
                  @keydown.native="handleEditRowKeyDown(props.row, $event)"
                >
                  <template v-if="!props.row.isTemporary || editedRow.activeSymbol">
                    <input
                      v-model="editedRow.costBasis"
                      class="tw-w-full tw-px-2 tw-py-1 tw-border tw-border-gray-300"
                      type="number"
                      placeholder="Cost Basis"
                      step="0.01"
                    >
                    <input
                      v-model="editedRow.purchaseDate"
                      class="tw-w-full tw-px-2 tw-py-1 tw-border tw-border-gray-300"
                      type="date"
                      placeholder="Purchase Date"
                    >
                  </template>
                </Stack>
              </template>
              <template v-else>
                <strong>{{ toCurrency(props.row.costBasis) || '—' }}</strong><br>
                <span>{{ props.row.purchaseDate || '—' }}</span>
              </template>
            </div>
          </o-table-column>


          <!-- Shares -->
          <o-table-column
            v-slot="props"
            field="shares"
            label="Shares"
            position="right"
            class="tw-text-left"
            :width="110"
            sortable
          >
            <div class="tw-relative tw-text-left">
              <template v-if="editingRow === props.row.id">
                <!-- Only render the Shares input if this row is not new or a ticker has been selected -->
                <template v-if="!props.row.isTemporary || editedRow.activeSymbol">
                  <input
                    v-model="editedRow.shares"
                    class="tw-w-full tw-px-2 tw-py-1 tw-border tw-border-gray-300"
                    type="number"
                    step="0.01"
                    placeholder="# Shares"
                    @keydown="handleEditRowKeyDown(props.row, $event)"
                  >
                </template>
              </template>
              <template v-else>
                <strong class="stk-size-3_5">{{ isNaN(parseFloat(props.row.shares)) ? props.row.shares : parseFloat(props.row.shares).toFixed(2) }}</strong>
              </template>
            </div>
          </o-table-column>

          <!-- Security Type -->
          <o-table-column
            v-slot="props"
            field="securityType"
            label="Security Type"
            :width="120"
            sortable
          >
            <div class="tw-relative">
              <template v-if="editingRow === props.row.id">
                <!-- Only render if it's not a new holding or a ticker has been selected -->
                <template v-if="!props.row.isTemporary || editedRow.activeSymbol">
                  <input
                    v-model="editedRow.securityType"
                    class="tw-w-full tw-px-2 tw-py-1 tw-border tw-border-gray-300 disabled-input"
                    disabled
                  >
                </template>
              </template>
              <template v-else>
                <span>{{ props.row.securityType }}</span>
              </template>
            </div>
          </o-table-column>

          <!-- Expense Ratio -->
          <o-table-column
            v-slot="props"
            field="expenseRatio"
            label="Expense Ratio"
            position="left"
            :width="80"
            sortable
          >
            <div class="tw-relative">
              <template v-if="editingRow === props.row.id">
                <!-- Only render if not a new holding or a ticker has been selected -->
                <template v-if="!props.row.isTemporary || editedRow.activeSymbol">
                  <input
                    v-model="editedRow.expenseRatio"
                    class="tw-w-full tw-px-2 tw-py-1 tw-border tw-border-gray-300 disabled-input"
                    disabled
                  >
                </template>
              </template>
              <template v-else>
                <p class="tw-text-left">
                  {{ toPercent(props.row.expenseRatio) }}
                </p>
              </template>
            </div>
          </o-table-column>

          <!-- Account -->
          <o-table-column
            v-slot="props"
            field="accountSort"
            label="Account and Portfolio"
            sortable
          >
            <div class="tw-relative">
              <template v-if="editingRow === props.row.id">
                <!-- Only render the account/portfolio inputs if this row is not new or a ticker has been selected -->
                <template v-if="!props.row.isTemporary || editedRow.activeSymbol">
                  <Stack
                    gap="2"
                    @keydown.native="handleEditRowKeyDown(props.row, $event)"
                  >
                    <div class="tw-flex tw-items-center">
                      <input
                        v-model="editedRow.account"
                        class="tw-w-full tw-px-2 tw-py-1 tw-border tw-border-gray-300"
                        placeholder="Account Name"
                        @input="checkAccountFieldChange()"
                      >
                    </div>
                    <select
                      v-model="editedRow.portfolioName"
                      class="tw-w-full tw-px-2 tw-py-1 tw-border tw-border-gray-300"
                      :disabled="!editedRow.isTemporary"
                      @change="update_primary_entity_external_id"
                    >
                      <option
                        disabled
                        value=""
                      >
                        Select a portfolio
                      </option>
                      <option
                        v-for="option in portfolioOptions"
                        :key="option.id"
                        :value="option.name"
                      >
                        {{ option.name }}
                      </option>
                    </select>
                  </Stack>
                </template>
              </template>
              <template v-else>
                <p>{{ props.row.account || '—' }}</p>
                <span class="italic">
                  <p>{{ props.row.portfolioName || '—' }}</p>
                </span>
              </template>
            </div>
          </o-table-column>


          <!-- Account Registration -->
          <o-table-column
            v-slot="props"
            field="accountRegSort"
            label="Account Registration and Tax Status"
            :width="180"
            sortable
          >
            <div class="tw-relative">
              <template v-if="editingRow === props.row.id">
                <!-- Only render the registration and tax status inputs if not a new holding or a ticker is selected -->
                <template v-if="!props.row.isTemporary || editedRow.activeSymbol">
                  <Stack
                    gap="2"
                    @keydown.native="handleEditRowKeyDown(props.row, $event)"
                  >
                    <div class="tw-flex tw-items-center">
                      <input
                        v-model="editedRow.accountRegistration"
                        class="tw-w-full tw-px-2 tw-py-1 tw-border tw-border-gray-300"
                        placeholder="Account Registration"
                        @input="checkAccountFieldChange()"
                      >
                    </div>
                    <select
                      v-model="editedRow.accountTaxStatus"
                      class="tw-w-full tw-px-2 tw-py-1 tw-border tw-border-gray-300"
                      @change="checkAccountFieldChange()"
                    >
                      <option value="Unclassified">
                        Select Tax Status
                      </option>
                      <option value="Taxable">
                        Taxable
                      </option>
                      <option value="Tax Deferred">
                        Tax Deferred
                      </option>
                      <option value="Tax Free">
                        Tax Free
                      </option>
                    </select>
                  </Stack>
                </template>
              </template>
              <template v-else>
                <p>{{ props.row.accountRegistration || '—' }}</p>
                <span class="italic">
                  <p>{{ props.row.accountTaxStatus || '—' }}</p>
                </span>
              </template>
            </div>
          </o-table-column>
          <o-table-column
            v-if="hasNotes"
            v-slot="props"
            field="notes"
            label="Notes"
            sortable
          >
            <div class="tw-relative">
              <div>
                <p>{{ props.row.notes || '—' }}</p>
              </div>
            </div>
          </o-table-column>
        </o-table>
      </div>

      <!-- Account Change Notice -->
      <div
        v-if="accountFieldsChanged"
        class="tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-bg-blue-400 tw-p-4 tw-border-t tw-border-blue-200"
      >
        <div
          class="tw-container tw-mx-auto tw-flex tw-justify-between tw-items-center"
        >
          <div>
            <p class="tw-font-medium">
              You've made changes to account information.
            </p>
            <o-checkbox
              v-model="applyToAllHoldings"
              class="custom-checkbox"
            >
              Apply account detail changes to all holdings for this account
            </o-checkbox>
          </div>
        </div>
      </div>

      <!-- Analytics Warning Notifications -->
      <div
        v-if="showAnalyticsWarning || showTemporaryWarning"
        class="tw-fixed tw-top-0 tw-inset-x-0 tw-flex tw-justify-center tw-z-30 tw-p-5 tw-bg-gray-300"
      >
        <!-- Persistent notification from MagicTickerFill -->
        <div
          v-if="showAnalyticsWarning"
          class="notification is-warning"
          role="alert"
        >
          <button
            class="delete"
            aria-label="Close"
            @click="showAnalyticsWarning = false"
          />
          <p>
            Run Analytics on the "Start" tab to update analytics and spreadsheet
            downloads.
          </p>
        </div>

        <!-- Temporary notification after holding edit. Temporary for editing because the advisor will be looking at the screen while editing, but might click away during MTF -->
        <div
          v-if="showTemporaryWarning"
          class="notification is-warning"
          role="alert"
        >
          <button
            class="delete"
            aria-label="Close"
            @click="showTemporaryWarning = false"
          />
          <p>
            Run Analytics on the "Start" tab to update analytics and spreadsheet
            downloads.
          </p>
        </div>
      </div>

      <!-- Save Status Messages -->
      <div
        v-if="saveMessage"
        class="tw-fixed tw-top-1/2 tw-left-1/2 tw-transform tw--translate-x-1/2 tw--translate-y-1/2 tw-p-4 tw-rounded tw-shadow-lg tw-z-50"
        :class="saveSuccess
          ? 'tw-bg-green-100 tw-border tw-border-green-300'
          : 'tw-bg-red-100 tw-border tw-border-red-300'"
      >
        <div class="tw-flex tw-items-center">
          <i
            :class="[
              'fas',
              saveSuccess
                ? 'fa-check tw-text-green-600'
                : 'fa-times tw-text-red-600',
              'tw-mr-2',
            ]"
          />
          <span class="tw-text-black">{{ saveMessage }}</span>
        </div>
      </div>


      <!-- Deletion Confirmation Modal -->
      <StakeModal
        v-model="showDeleteConfirmation"
        :width="400"
      >
        <Stack
          gap="2"
          class="stk-p-4"
        >
          <Title>Confirm Deletion</Title>
          <p>Are you sure you want to delete this holding?</p>
          <Buttons>
            <Button
              @click="showDeleteConfirmation = false"
            >
              Cancel
            </Button>
            <Button
              :disabled="isDeleting"
              variant="danger"
              @click="deleteHolding"
            >
              {{ isDeleting ? "Deleting..." : "Delete" }}
            </Button>
          </Buttons>
        </Stack>
      </StakeModal>

      <StakeModal v-model="showBulkDeleteConfirmation">
        <Stack
          gap="2"
          class="stk-p-4"
        >
          <Title>Confirm Deletion</Title>
          <p>
            Are you sure you want to delete
            {{ selectedHoldingIds.length }} holdings?
          </p>
          <Buttons>
            <Button
              @click="showBulkDeleteConfirmation = false"
            >
              Cancel
            </Button>
            <Button
              :disabled="isBulkDeleting"
              variant="danger"
              @click="bulkDeleteSelectedHoldings"
            >
              <i
                v-if="isBulkDeleting"
                class="fas fa-spinner fa-spin tw-mr-2"
              />
              Delete Selected
            </Button>
          </Buttons>
        </Stack>
      </StakeModal>

      <!-- Spreadsheet Export Component -->
      <SpreadsheetExport
        v-if="!holdingsloading"
        ref="spreadsheetExport"
        @exports-updated="onExportsUpdated"
      />
    </Stack>
    <StakeModal v-model="showCSVModal">
      <UploadHoldingsCSV
        :entity="entity"
        :can-edit="can_edit"
        @uploaded="handleUploadComplete"
      />
    </StakeModal>
  </div>
</template>

<script>
import * as Sentry from '@sentry/vue';
import SearchTickers from '@frontend/ui/components/utils/SearchTickers.vue';
import PortfolioSummaryTree from '@frontend/ui/features/checkup/components/PortfolioSummaryTree.vue';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore.js';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
import { Button, Buttons, Level, LevelLeft, LevelRight, Stack, StakeModal, Subtitle, Title, Toggle } from '@stakeui';
import { mapStores } from 'pinia';
import MagicTickerFill from './MagicTickerFill.vue';
import SpreadsheetExport from './SpreadsheetExport.vue';
import UploadHoldingsCSV from '../../holdings/UploadHoldingsCSV.vue';

export default {
  name: 'PortfolioHoldingsArea',
  components: {
    PortfolioSummaryTree,
    MagicTickerFill,
    SpreadsheetExport,
    SearchTickers,
    Button,
    Buttons,
    Level,
    LevelLeft,
    LevelRight,
    Stack,
    StakeModal,
    Subtitle,
    Title,
    Toggle,
    UploadHoldingsCSV,
  },
  props: {
    can_edit: {
      type: Boolean,
      default: true,
      // For now making this true because that replicates functionality we currently have for holdings tab of advisorcore.
      // Note we still check the backend for permissions so it's not such a big deal.
    },
    entity: {
      type: Object,
    },
    showReplaceButton: {
      type: Boolean,
      default: false,
    },
    showAddButton: {
      type: Boolean,
      default: false,
    },
    showDownloadButton: {
      type: Boolean,
      default: false,
    },
    downloadDisabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showAnalyticsWarning: false,
      showTemporaryWarning: false,
      showCSVModal: false,
      temporaryWarningTimer: null,
      summaryTree: {},
      holdingsloading: true,
      editingRow: null,
      originalRow: null,
      editedRow: {},
      isSaving: false,
      saveSuccess: false,
      saveMessage: '',
      openTickerSearch: false,
      validationErrors: {},
      applyToAllHoldings: true,
      accountFieldsChanged: false,
      hasSavedChanges: false,
      showDeleteConfirmation: false,
      holdingToDelete: null,
      isCreating: false,
      isDeleting: false,
      isBulkDeleting: false,
      marketValueFlash: false,
      localHoldings: [],
      tableKey: 0,
      lastCreatedHoldingId: null,
      deletedHoldingIds: [], // used to track deleted holdings in this session so that we can have immediate feedback in the UI
      optimisticNewHoldings: [], // used to track new holdings created in this session so that we can immediately add them to the table without waiting for a refresh
      optimisticEdits: {}, // used to track edits made to existing holdings in this session
      showAllTaxLots: false, // Toggle for showing individual tax lots vs aggregated positions
      expandedPositions: {}, // Track which aggregated positions are expanded
      selectedHoldingIds: [],
      showBulkDeleteConfirmation: false,
    };
  },
  computed: {
    ...mapStores(useCheckupStore),
    ...mapStores(useAdvisorStore),
    headers() {
      return {
        'X-CSRFToken': this.$root.$data.csrfToken,
      };
    },
    inCheckup() {
      return window.location.href.includes('auto-portfolio');
    },
    tableData() {
      const mappedRemote = this.checkupStore.holdingsFromPortfolios.map(holding => {
        return this.normalizeHolding(holding);
      });

      if (mappedRemote.length === 0) {
        this.holdingsloading = false;
      }

      // Prioritize lastCreatedHoldingId
      mappedRemote.sort((a, b) => {
        // Primary sort: group by symbol + account + portfolio
        // Use uploadedSymbol when available so proxied bonds with different CUSIPs sort separately
        const keyA = `${a.uploadedSymbol || a.activeSymbol}_${a.account || 'no-account'}_${a.portfolioName || 'no-portfolio'}`;
        const keyB = `${b.uploadedSymbol || b.activeSymbol}_${b.account || 'no-account'}_${b.portfolioName || 'no-portfolio'}`;

        if (keyA !== keyB) {
          return keyA.localeCompare(keyB);
        }
      });

      const filteredRemote = mappedRemote.filter(
          holding => !this.deletedHoldingIds.includes(holding.id),
      );

      const filteredOptimisticNewHoldings = this.optimisticNewHoldings.filter(
          holding => !this.deletedHoldingIds.includes(holding.id),
      );

      const remoteWithEdits = filteredRemote.map(h =>
        this.optimisticEdits[h.id] ?? h,
      );

      const optimisticWithEdits = filteredOptimisticNewHoldings.map(h =>
        this.optimisticEdits[h.id] ?? h,
      );

      // Combine optimistic new holdings (which should already be normalized when added),
      // localHoldings, and the filtered remote holdings
      const combined = [
        ...this.localHoldings,
        ...optimisticWithEdits,
        ...remoteWithEdits,
      ];

      // If showing all tax lots, return the full list
      if (this.showAllTaxLots) {
        return combined;
      }

      // Otherwise, aggregate by symbol + account + portfolio
      return this.aggregateHoldings(combined);
    },
    selectableRows() {
      return this.tableData.filter(row => this.isRowSelectable(row));
    },
    allSelectableRowsSelected() {
      if (!this.selectableRows.length) return false;
      return this.selectableRows.every(row => this.selectedHoldingIds.includes(row.id));
    },
    someSelectableRowsSelected() {
      if (!this.selectedHoldingIds.length) return false;
      return !this.allSelectableRowsSelected;
    },
    hasNotes() {
      return this.tableData.some(
          r => r.notes && r.notes.toString().trim().length > 0,
      );
    },
    tableRenderKey() {
      // stringify both parts so Vue always gets a different key
      // whenever either the sort‑reset counter OR the presence
      // of the Notes column changes.
      return `${this.tableKey}-${this.hasNotes}`;
    },
    portfolioOptions() {
      if (this.tableData.length > 0) {
        const optionsMap = {};
        this.tableData.forEach(row => {
          if (row.portfolioName) {
            optionsMap[row.portfolioName] = row.primary_entity_external_id;
          }
        });
        // Convert the map into an array of option objects
        return Object.keys(optionsMap).map(name => ({
          name,
          id: optionsMap[name],
        }));
      } else {
        // No rows? Use the checkupStore portfolio where label === "portfolio"
        const portfolio = this.checkupStore.portfolios.find(p => p.label === 'portfolio');
        return portfolio ? [{ name: portfolio.name, id: portfolio.external_id }] : [];
      }
    },
    coverageWarnings() {
      const portfolio = this.checkupStore.portfolio;
      if (!portfolio) return [];

      const coverage = [
        {
          analyticsType: 'Risk',
          value: portfolio.trailing_returns_monthly_rebalance?.[2],
        },
        {
          analyticsType: 'Trailing Returns',
          value:
            portfolio.trailing_returns_monthly_rebalance?.[1]
                ?.trailing_1_yr_returns,
        },
        {
          analyticsType: 'Trailing Volatility',
          value: portfolio.trailing_volatilities?.[1]?.trailing_1_yr_volatility,
        },
      ];

      return coverage
          .filter(c => (c.value && c.value < 0.99) || c.value === 0)
          .map(c => c.analyticsType);
    },

    numberUnmatchedHoldings() {
      const holdings = this.checkupStore.holdingsFromPortfolios;
      if (!holdings || !Array.isArray(holdings)) return 0;

      // Count the number of holdings where 'found' is false
      return holdings.reduce((count, holding) => {
        if (holding.found === false) {
          count++;
        }
        return count;
      }, 0);
    },

    hasSyncedPortfolio() {
      return this.checkupStore.portfolios.some(
          p => p.holdings_synced === true,
      );
    },

    hasAggregatedPositions() {
      // Simple check: are there any holdings with the same symbol + account?
      const holdings = this.checkupStore.holdingsFromPortfolios.map(holding => {
        return this.normalizeHolding(holding);
      });

      const groups = {};
      holdings.forEach(holding => {
        const key = `${holding.uploadedSymbol || holding.activeSymbol}_${holding.account || 'no-account'}`;
        groups[key] = (groups[key] || 0) + 1;
      });

      return Object.values(groups).some(count => count > 1);
    },
  },
  watch: {
    'checkupStore.uploadedDFByPortfolio': {
      handler() {
        if (this.$refs.spreadsheetExport) {
          this.$refs.spreadsheetExport.updateHoldingsExports();
        }
      },
      deep: true,
    },

    'editedRow.portfolioName'(newName) {
      const option = this.portfolioOptions.find(o => o.name === newName);
      if (option) {
        this.editedRow.primary_entity_external_id = option.id;
      }
    },

    tableData(newData) {
      this.generateSummaryTree();
      const availableIds = newData.map(row => row.id);
      this.selectedHoldingIds = this.selectedHoldingIds.filter(id => availableIds.includes(id));
    },

    editingRow(newValue) {
      // Reset the account fields changed flag when starting a new edit
      if (newValue !== null) {
        this.accountFieldsChanged = false;
      }
    },

    showAllTaxLots(newValue) {
      // Clear expanded positions when toggling modes
      this.expandedPositions = {};
      // Force table re-render
      this.tableKey++;
    },
  },
  mounted() {
    this.generateSummaryTree();
  },
  methods: {
    normalizeHolding(holding) {
      const holdingPortfolioName = holding.portfolioName || holding.primary_entity_name;
      const holdingAccount = holding.account || this.getAcct(holding);
      const holdingAccountReg = holding.accountRegistration || this.getAcctReg(holding);
      const holdingAccountTax = holding.accountTaxStatus || this.getAcctTaxStatus(holding);
      return {
        portfolioName: holdingPortfolioName,
        primary_entity_external_id: holding.primary_entity_external_id,
        id: holding.id || holding.upload_id,
        activeSymbol: holding.activeSymbol || this.getTickerField(holding, 'symbol'),
        uploadedSymbol: holding.uploadedSymbol || holding.uploadedsymbol || this.getUploadedSymbol(holding),
        name: holding.name || this.getName(holding),
        marketValue: holding.marketValue || holding.dollars,
        shares: holding.shares || holding.number_of_shares,
        costBasis: holding.costBasis || holding.costbasis,
        purchaseDate: holding.purchaseDate || this.getPurchDate(holding),
        expenseRatio: holding.expenseRatio || this.getExpenseRatio(holding),
        securityType: holding.securityType || this.getSecurityType(holding),
        account: holdingAccount,
        accountRegistration: holdingAccountReg,
        accountTaxStatus: holdingAccountTax,
        updated_at: holding.updated_at,
        matched: holding.matched !== undefined ? holding.matched : (holding.found === false ? false : true),
        notes: holding.notes || this.getNotes(holding),
        entity_type: holding.entity_type || this.getEntityType(holding.primary_entity_external_id),
        upload_id: holding.upload_id || holding.upload_id,
        source_document_id: holding.source_document_id,
        accountSort: ((holdingPortfolioName || '') + (holdingAccount || '')).toLowerCase(),
        accountRegSort: ((holdingAccountTax || '') + (holdingAccountReg || '')).toLowerCase(),
      };
    },
    useSymbol(editedRow) {
      return editedRow.activeSymbol || editedRow.uploadedSymbol;
    },
    onHoldingsUpdated() {
      // For MagicTickerFill - show persistent warning only if URL contains "auto-portfolio"
      console.error('IN CHECKUP', this.inCheckup);
      if (this.inCheckup) {
        this.showAnalyticsWarning = true;
      }
    },
    update_primary_entity_external_id() {
      const option = this.portfolioOptions.find(o => o.name === this.editedRow.portfolioName);
      if (option) {
        this.editedRow.primary_entity_external_id = option.id;
      }
    },

    onMTFFilesUpdated(files) {
      console.log('MTF files updated:', files);
      // Any additional handling if needed
    },

    onExportsUpdated(exports) {
      console.log('Exports updated:', exports);
      // Any additional handling if needed
    },
    async handleUploadComplete(entityId) {
      this.showCSVModal = false;
      await this.updatePortfolioHoldings();
    },

    // Inline editing methods
    startEdit(row) {
      if (!this.can_edit) return;

      // If it's an aggregated position or child tax lot, we need to switch to show all tax lots first
      if (row.isAggregated || row.isChildTaxLot) {
        this.showAllTaxLots = true;
        // Wait for the view to update before starting edit
        this.$nextTick(() => {
          // Use the row we have but restore the original ID (strip off any _lot_X suffix)
          const originalId = row.id.toString().replace(/_lot_\d+$/, '');
          const actualRow = { ...row, id: originalId };

          this.editingRow = actualRow.id;
          this.editedRow = { ...actualRow };
          this.originalRow = { ...actualRow };
          this.accountFieldsChanged = false;
        });
      } else {
        // For non-aggregated holdings, we can edit directly regardless of the current view mode
        this.editingRow = row.id;
        this.editedRow = { ...row };
        this.originalRow = { ...row };
        this.accountFieldsChanged = false;
      }
    },
    addNewHolding() {
      if (!this.can_edit) return;
      const portfolio = this.checkupStore.portfolios.find(p => p.label === 'portfolio');
      const newId = `temp-${Date.now()}`;

      const newRow = {
        id: newId,
        primary_entity_external_id: portfolio.external_id,
        portfolioName: portfolio.name,
        activeSymbol: '',
        uploadedSymbol: '',
        name: '',
        marketValue: '',
        shares: '',
        costBasis: '',
        purchaseDate: null,
        expenseRatio: '',
        securityType: '',
        account: '',
        accountRegistration: '',
        accountTaxStatus: 'Unclassified',
        matched: false,
        isTemporary: true,
      };

      // Insert new row at the top of the table data array
      this.localHoldings.unshift(newRow);

      // Reset the table's sort state by updating the key
      this.tableKey++;

      // Switch to edit mode for that row
      this.editingRow = newId;
      this.editedRow = { ...newRow };
      this.originalRow = { ...newRow };

      // Let Vue finish rendering, then focus the SearchTickers input
      this.$nextTick(() => {
        if (this.$refs.tickerSearch) {
          this.$refs.tickerSearch.focusInput();
        }
      });
    },

    isRowSelectable(row) {
      return !row.isChildTaxLot;
    },
    isRowSelected(row) {
      return this.selectedHoldingIds.includes(row.id);
    },
    toggleRowSelection(row, event) {
      if (!this.isRowSelectable(row)) return;
      const checked = event?.target?.checked;
      if (checked) {
        if (!this.selectedHoldingIds.includes(row.id)) {
          this.selectedHoldingIds.push(row.id);
        }
      } else {
        this.selectedHoldingIds = this.selectedHoldingIds.filter(id => id !== row.id);
      }
    },
    toggleSelectAllRows() {
      if (this.allSelectableRowsSelected) {
        this.selectedHoldingIds = [];
        return;
      }
      this.selectedHoldingIds = this.selectableRows.map(row => row.id);
    },
    getSelectedRowsForDelete() {
      return this.tableData.filter(row => this.selectedHoldingIds.includes(row.id));
    },
    expandRowsForDelete(rows) {
      const expanded = [];
      rows.forEach(row => {
        if (row.isAggregated && Array.isArray(row.taxLots)) {
          expanded.push(...row.taxLots);
        } else if (!row.isChildTaxLot) {
          expanded.push(row);
        }
      });
      return expanded;
    },
    dedupeDeleteRows(rows) {
      const seen = new Set();
      const unique = [];
      rows.forEach(row => {
        const rowId = row.id || row.upload_id;
        if (!rowId) return;
        const key = `${rowId}-${row.upload_id || ''}`;
        if (!seen.has(key)) {
          seen.add(key);
          unique.push(row);
        }
      });
      return unique;
    },
    async deleteHoldings(rows, { clearSelection = true } = {}) {
      if (!this.can_edit) return false;
      const expandedRows = this.expandRowsForDelete(rows);
      const targets = this.dedupeDeleteRows(expandedRows);
      if (!targets.length) return true;

      const totalExisting =
        this.checkupStore.holdingsFromPortfolios.length + this.localHoldings.length;
      const willBeEmpty = targets.length >= totalExisting;
      let hadError = false;

      for (const row of targets) {
        const isTemporary = row.isTemporary || (row.id && String(row.id).startsWith('temp'));
        if (isTemporary) {
          this.localHoldings = this.localHoldings.filter(holding => holding.id !== row.id);
          continue;
        }

        const deletedId = row.id;
        if (!this.deletedHoldingIds.includes(deletedId)) {
          this.deletedHoldingIds.push(deletedId);
        }

        const url = `/api/v1/portfolios/${row.primary_entity_external_id}/delete-holding`;
        const requestBody = {
          id: deletedId,
          upload_id: row.upload_id,
        };

        try {
          const response = await fetch(url, {
            method: 'DELETE',
            headers: {
              ...this.headers,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
          });

          if (!response.ok) {
            this.deletedHoldingIds = this.deletedHoldingIds.filter(id => id !== deletedId);
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to delete holding');
          }
        } catch (error) {
          Sentry.captureException(error);
          hadError = true;
          this.saveSuccess = false;
          this.saveMessage = error.message || 'Deletion failed. Please try again.';
        }
      }

      if (!hadError) {
        this.saveSuccess = true;
        this.saveMessage = 'Holdings deleted successfully!';
      }

      setTimeout(() => {
        this.saveMessage = '';
      }, 1500);

      if (clearSelection) {
        const deletedIds = targets.map(row => row.id);
        this.selectedHoldingIds = this.selectedHoldingIds.filter(id => !deletedIds.includes(id));
      }

      if (willBeEmpty) {
        this.$emit('deleted-last-holding');
      }

      return !hadError;
    },
    async bulkDeleteSelectedHoldings() {
      if (!this.selectedHoldingIds.length) return;
      this.isBulkDeleting = true;
      const rows = this.getSelectedRowsForDelete();
      const success = await this.deleteHoldings(rows);
      if (success) {
        this.showBulkDeleteConfirmation = false;
      }
      this.isBulkDeleting = false;
    },
    async deleteAllHoldings() {
      this.isBulkDeleting = true;
      const entityId = this.entity?.external_id;
      const holdings = this.checkupStore.holdingsFromPortfolios
          .filter(holding => !entityId || holding.primary_entity_external_id === entityId)
          .map(holding => this.normalizeHolding(holding));
      const rows = [...this.localHoldings, ...holdings];
      const success = await this.deleteHoldings(rows);
      this.isBulkDeleting = false;
      return success;
    },

    // Delete holding flow
    confirmDelete(row) {
      if (!this.can_edit) return;
      if (row.isTemporary || (row.id && String(row.id).startsWith('temp'))) {
        // Immediately remove the temporary holding without confirmation
        this.localHoldings = this.localHoldings.filter(
            holding => holding.id !== row.id,
        );
      } else {
        // For permanent holdings, store the row and show the confirmation modal
        this.holdingToDelete = row;
        this.showDeleteConfirmation = true;
      }
    },
    async deleteHolding() {
      if (!this.can_edit || !this.holdingToDelete) return;

      const willBeEmpty = this.tableData.filter(h => h.id !== this.holdingToDelete.id).length === 0;

      const primaryEntityExternalID = this.holdingToDelete.primary_entity_external_id;
      let deletedId = null;
      deletedId = this.holdingToDelete.id;

      // Optimistically mark the holding as deleted
      if (!this.deletedHoldingIds.includes(deletedId)) {
        this.deletedHoldingIds.push(deletedId);
      }

      // Show the deletion success message immediately
      this.saveSuccess = true;
      this.saveMessage = 'Holding deleted successfully!';

      // Close the deletion confirmation modal immediately
      this.showDeleteConfirmation = false;
      const url = `/api/v1/portfolios/${primaryEntityExternalID}/delete-holding`;
      const requestBody = {
        id: deletedId,
        upload_id: this.holdingToDelete.upload_id,
      };

      try {
        const response = await fetch(url, {
          method: 'DELETE',
          headers: {
            ...this.headers,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          // Optionally, roll back the optimistic update by removing from deletedHoldingIds
          this.deletedHoldingIds = this.deletedHoldingIds.filter(id => id !== deletedId);
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to delete holding');
        }

        // Optionally, you could refresh other aspects in the background if needed,
        // but for immediate feedback the optimistic deletion is enough.
      } catch (error) {
        Sentry.captureException(error);
        this.saveSuccess = false;
        this.saveMessage = error.message || 'Deletion failed. Please try again.';
      } finally {
        const deletedId = this.holdingToDelete?.id;
        setTimeout(() => {
          this.saveMessage = '';
        }, 1500);
        this.isDeleting = false;
        this.holdingToDelete = null;
        if (deletedId) {
          this.selectedHoldingIds = this.selectedHoldingIds.filter(
              id => id !== deletedId,
          );
        }
        if (willBeEmpty) {
          this.$emit('deleted-last-holding');
        }
      }
    },
    cancelEdit() {
      // If this is a newly added row (not saved to the database yet)
      if (this.editedRow.isTemporary) {
        this.localHoldings = this.localHoldings.filter(
            h => h.id !== this.editedRow.id,
        );
      }

      // Reset the editing state
      this.editingRow = null;
      this.editedRow = {};
      this.originalRow = null;
      this.validationErrors = {};
      this.accountFieldsChanged = false;
    },
    async saveRow(row) {
      this.validationErrors = {};
      let hasErrors = false;
      const isTemporary = row.id?.toString().startsWith('temp') || row.isTemporary;
      const wasEmpty = this.tableData.length === 1 && this.tableData[0].id === row.id;
      const primaryEntityExternalID = this.editedRow.primary_entity_external_id;

      if (!this.editedRow.marketValue) {
        this.validationErrors.marketValue = 'Market Value is required';
        hasErrors = true;
      }
      // If editing a non-matched holding, we shouldn't validate on active symbol.
      if (!this.editedRow.activeSymbol && this.editedRow.matched === true && !isTemporary) {
        this.validationErrors.activeSymbol = 'Symbol/CUSIP is required';
        hasErrors = true;
      } else if (!this.editedRow.activeSymbol && isTemporary) {
        this.validationErrors.activeSymbol = 'Symbol/CUSIP is required';
        hasErrors = true;
      }
      if (hasErrors) return;

      this.isSaving = true;


      // For edits, stash an optimistic copy before the network call
      if (!isTemporary) {
        const optimistic = this.normalizeHolding({
          ...this.editedRow,
          id: row.id,
          updated_at: new Date().toISOString(),
          upload_id: row.upload_id,
        });
        this.$set(this.optimisticEdits, row.id, optimistic);
      }

      try {
        const url = isTemporary ?
          `/api/v1/portfolios/${primaryEntityExternalID}/create-holding` :
          `/api/v1/portfolios/${primaryEntityExternalID}/edit-holding`;
        const body = isTemporary ?
          JSON.stringify(this.editedRow) :
          JSON.stringify({ orig: this.originalRow, new: this.editedRow, applyAllAccounts: this.applyToAllHoldings });

        const res = await fetch(url, {
          method: 'POST',
          headers: { ...this.headers, 'Content-Type': 'application/json' },
          body,
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.error || 'Failed to save changes');
        }
        const { holding } = await res.json();

        if (isTemporary) {
          // New‐holding flow
          const newH = this.normalizeHolding(holding);
          newH._isOptimistic = true;
          newH.updated_at = newH.updated_at || new Date().toISOString();
          this.optimisticNewHoldings.push(newH);
          this.lastCreatedHoldingId = newH.id;
          // remove the blank temp‐row
          this.localHoldings = this.localHoldings.filter(h => h.id !== row.id);
        } else {
          // fire your analytics warning right away:
          this.$set(this.optimisticEdits, holding.id, this.normalizeHolding(holding));
          if (this.inChckup) {
            this.showTemporaryAnalyticsWarning();
          }
        }

        this.saveSuccess = true;
        this.saveMessage = isTemporary ? 'New holding created!' : 'Changes saved!';
        setTimeout(() => {
          this.saveMessage = '';
        }, 1500);
      } catch (error) {
        console.error('Error saving row:', error);
        this.saveSuccess = false;
        this.saveMessage = error;
        setTimeout(() => {
          this.saveMessage = '';
        }, 5000);
        // Roll back optimistic edit on failure
        if (!isTemporary) this.$delete(this.optimisticEdits, row.id);
      } finally {
        if (this.editedRow.activeSymbol && this.editedRow.matched === false && !isTemporary) {
          // If we found a match for something that was previously unmatched, we have to pull the new holding from the server to get its shareownership_id to allow for editing of that holding
          // Ideally we would just pull that one holding, but that would require new code, and it's simpler from a code perspective to just repull all.
          // Though this strategy is slower, it is less error-prone and easier to maintain.
          await this.updatePortfolioHoldings();
        } else if (this.accountFieldsChanged && this.applyToAllHoldings) {
          // If the account fields change and the user has selected to apply to all holdings, we need to update the other holdings, so we need to repull all.
          await this.updatePortfolioHoldings();
        }
        this.isSaving = false;
        this.editingRow = null;
        this.editedRow = {};
        this.originalRow = null;
        this.accountFieldsChanged = false;
        this.openTickerSearch = false;
        this.$emit('holding-saved', this.editedRow);
        if (wasEmpty) {
          console.error('WAS EMPTY YES');
          this.$emit('created-first-holding');
        }
      }
    },
    handleEditRowKeyDown(row, event) {
      // If Enter is pressed...
      if (event.key === 'Enter') {
        // Get the internal element of the SearchTickers component
        const tickerSearchEl = this.$refs.tickerSearch && this.$refs.tickerSearch.$el;
        // If the focused element is within the ticker search, do nothing
        if (tickerSearchEl && tickerSearchEl.contains(document.activeElement)) {
          return; // Let SearchTickers handle enter (like selecting the first result)
        }
        // Otherwise, prevent the default action and call saveRow for this row.
        event.preventDefault();
        this.saveRow(row);
      }
    },

    checkAccountFieldChange() {
      // Check if any account fields have changed
      if (
        this.originalRow.account !== this.editedRow.account ||
        this.originalRow.accountRegistration !== this.editedRow.accountRegistration ||
        this.originalRow.accountTaxStatus !== this.editedRow.accountTaxStatus
      ) {
        this.accountFieldsChanged = true;
      } else {
        this.accountFieldsChanged = false;
      }
    },

    handleTickerSelect(ticker) {
      // Set ticker-related fields
      this.editedRow.activeSymbol = ticker.symbol;
      this.editedRow.name = ticker.full_name;
      this.editedRow.securityType = ticker.asset_type;
      if (ticker.asset_type === 'Stock' && (ticker.expense_ratio === null || ticker.expense_ratio === '')) {
        this.editedRow.expenseRatio = '0.00%';
      } else if (ticker.expense_ratio === null || ticker.expense_ratio === '') {
        this.editedRow.expenseRatio = '';
      } else {
        this.editedRow.expenseRatio = String(ticker.expense_ratio * 100) + '%';
      }
      this.editedRow.ticker = ticker;
      this.openTickerSearch = false;

      // For new holdings, move focus to the Market Value input and flash it green
      if (this.editedRow.isTemporary) {
        this.$nextTick(() => {
          if (this.$refs.marketValueInput) {
            this.$refs.marketValueInput.focus();
            this.marketValueFlash = true;
            setTimeout(() => {
              this.marketValueFlash = false;
            }, 3000);
          }
        });
      }
    },

    handleTickerMatched(ticker) {
      this.editedRow.ticker = ticker;
      this.openTickerSearch = false;
    },

    formatCurrency(row, field) {
      const value = row[field];
      if (!value) return;

      const numericValue = parseFloat(
          value.toString().replace(/[^0-9.-]+/g, ''),
      );
      if (!isNaN(numericValue)) {
        row[field] = this.toCurrency(numericValue);
      }
    },

    getEntityType(entityId) {
      return this.checkupStore.portfolios.find(
          p => p.external_id === entityId,
      )?.entity_type;
    },

    // Show temporary warning for 5 seconds
    showTemporaryAnalyticsWarning() {
      // Clear any existing timer to prevent multiple notifications
      if (this.temporaryWarningTimer) {
        clearTimeout(this.temporaryWarningTimer);
      }

      // Show the temporary warning
      this.showTemporaryWarning = true;

      // Set timer to hide it after 5 seconds
      this.temporaryWarningTimer = setTimeout(() => {
        this.showTemporaryWarning = false;
      }, 8000);
    },

    toCurrency(value) {
      // If it's a string check the format
      if (typeof value === 'string') {
        // If it's already formatted just return it
        if (value.startsWith('$')) return value;

        // If it's not the right format, try to parse a float.
        value = parseFloat(value);
        if (isNaN(value)) return '';
      }

      const dollars = value?.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return dollars ? `$${dollars}` : '';
    },

    toPercent(value) {
      // If it's a string check the format
      if (typeof value === 'string') {
        // If it's already formatted just return it
        if (value.endsWith('%')) return value;

        // If it's not the right format, try to parse a float.
        value = parseFloat(value);
        if (isNaN(value)) return '';
      }

      const percent = value?.toLocaleString('en-US', {
        style: 'percent',
        maximumFractionDigits: 2,
      });
      return percent;
    },

    // Data formatting methods
    getUploadedSymbol(holding) {
      if (typeof holding.ticker === 'string' && holding.found === false) {
        return holding.ticker;
      } else {
        try {
          // The else-if is for CSV uploads or MTF cases when the uploaded name is different than the symbol used (i.e., proxy ticker)
          if (
            holding.metadata?.uploadedsymbol === holding.ticker.symbol &&
            holding.metadata?.notes
          ) {
            return holding.metadata.uploadedname;
          }
          return holding.uploadedsymbol || holding.metadata?.uploadedsymbol;
        } catch (error) {
          // If there is an error, default to returning holding.ticker if it exists, or '' if it doesn't
          return holding.ticker || holding.isin || holding.cusip || '';
        }
      }
    },

    getName(holding) {
      if (holding.uploadedname) {
        return holding.uploadedname;
      }
      if (
        holding.metadata?.uploadedname &&
        holding.metadata?.uploadedsymbol !== holding.ticker.symbol
      ) {
        return holding.metadata.uploadedname;
      }
      return this.getTickerField(holding, 'full_name');
    },

    getSecurityType(holding) {
      if (holding.securitytype) return holding.securitytype;
      return this.getTickerField(holding, 'asset_type');
    },

    getNotes(holding) {
      // get MTF notes if unmatched holding here
      if (holding.notes) {
        return holding.notes;
      }

      // if matched holding MTF notes stored in uploaded_df metadata
      return holding.metadata?.notes;
    },

    getPurchDate(holding) {
      // purchasedate in uploaded_df, purchase_date in shareownership.
      // Not sure if we ever do metadata purchasedate, but it was already here so I didn't get rid of it
      if (holding.purchasedate) {
        return holding.purchasedate;
      } else if (holding.purchase_date) {
        return holding.purchase_date;
      }

      // if matched holding stored in uploaded_df metadata
      return holding.metadata?.purchasedate;
    },

    getAcct(holding) {
      if (holding.account) {
        return holding.account;
      }
      return null;
    },

    getAcctTaxStatus(holding) {
      const accountTaxStatus = this.getPortfolioField(
          holding,
          'account_tax_status_string',
      );
      if (accountTaxStatus) {
        return accountTaxStatus;
      }
      // unmatchedh holdings have attributes stored in uploaded_df
      return holding.account_tax_status;
    },

    getAcctReg(holding) {
      // in shareownership
      if (holding.account_registration) {
        return holding.account_registration;
      }
      // in uploaded_df
      if (holding.accountregistration) {
        return holding.accountregistration;
      }
      return null;
    },

    getTickerField(holding, field) {
      if (typeof holding.ticker === 'object' && holding.ticker !== null) {
        return holding.ticker[field];
      }
      return null;
    },

    getExpenseRatio(holding) {
      if (typeof holding.ticker === 'object' && holding.ticker !== null) {
        if (this.getSecurityType(holding) === 'Stock' && (holding.ticker.expense_ratio === null || holding.ticker.expense_ratio === '')) {
          return 0;
        }
        return holding.ticker.expense_ratio;
      }
      return null;
    },

    getPortfolioField(holding, field) {
      if (typeof holding === 'object' && holding.ticker !== null) {
        return holding[field];
      }
      return null;
    },

    generateSummaryTree() {
      // Group table data by portfolio and account
      const portfolios = {};

      this.tableData.forEach(row => {
        // Ensure portfolio group exists
        if (!portfolios[row.portfolioName]) {
          portfolios[row.portfolioName] = { total: 0, accounts: {} };
        }

        // Handle undefined, blank, or missing account names
        const accountName = row.account?.trim() || 'Unspecified Account';

        // Ensure account group exists
        if (!portfolios[row.portfolioName].accounts[accountName]) {
          portfolios[row.portfolioName].accounts[accountName] = 0;
        }

        // Sum market values
        if (!row.isChildTaxLot) { // Exclude individual lots to prevent double counting
          portfolios[row.portfolioName].total += parseFloat(row.marketValue) || 0;
          portfolios[row.portfolioName].accounts[accountName] += parseFloat(row.marketValue) || 0;
        }
      });

      // Calculate overall total
      const overallTotal = Object.values(portfolios).reduce(
          (acc, portfolio) => acc + portfolio.total,
          0,
      );

      // Build the summary tree with all nodes expanded by default
      const tree = {
        name: 'Total',
        total: overallTotal,
        expanded: true, // Default to expanded
        children: Object.keys(portfolios).map(portfolioName => ({
          name: portfolioName,
          total: portfolios[portfolioName].total,
          expanded: false, // Default to expanded
          children: Object.keys(portfolios[portfolioName].accounts).map(
              accountName => ({
                name: accountName,
                total: portfolios[portfolioName].accounts[accountName],
                expanded: false, // Default to expanded
                children: [],
              }),
          ),
        })),
      };

      this.summaryTree = tree;

      const hasZeroRow = this.tableData.some(
          row => (parseFloat(row.marketValue) || 0) === 0,
      );
      if (overallTotal > 0 || hasZeroRow) {
        this.holdingsloading = false;
      }
    },

    toggleExpandCollapse(node, expand) {
      node.expanded = expand;
      node.children.forEach(child =>
        this.toggleExpandCollapse(child, expand),
      );
    },

    async updatePortfolioHoldings() {
      console.error('IN NEW UPDATE PORTFOLIO HOLDINGS');
      const newHoldings = {};

      // Doing it this way ensures that we also remove holdings when a portfolio is unselected
      for (const portfolio of this.checkupStore.portfolios) {
        console.error('portfolio here', portfolio);
        // If the id matches, this is the portfolio we want to refresh.
        // Or if null, we want to refresh all of them.
        if (portfolio.external_id) {
          console.error('Updating portfolio holdings', portfolio.external_id);
          const holdings = await this.getPortfolioHoldings(
              portfolio.external_id,
          );
          console.log('Holdings updated');
          newHoldings[portfolio.external_id] = holdings;
        }
        // Otherwise just copy them over
        else {
          console.error('Copying over existing holdings');
          newHoldings[portfolio.external_id] =
            this.checkupStore.holdingsByPortfolio[portfolio.external_id];
        }
      }

      // Update the holdings in the checkupStore
      this.checkupStore.holdingsByPortfolio = newHoldings;

      // Show temporary warning after holding edit
      if (this.hasSavedChanges) {
        console.error('Showing temporary analytics warning', this.inCheckup);
        if (this.inCheckup) {
          this.showTemporaryAnalyticsWarning();
        }
        this.hasSavedChanges = false;
      }
      this.optimisticNewHoldings = [];
      this.optimisticEdits = {};
    },

    async getPortfolioHoldings(externalID) {
      let url = `/api/v1/entities/${externalID}/holdings/`;
      if (this.inCheckup) {
        url = `/api/v1/entities/${externalID}/holdings/?in_checkup=true`;
      }
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          ...this.headers,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json();

      // Matched holdings
      const matched = data.portfolio.holdings;

      // Unmatched holdings
      const unmatched = data.portfolio.unmatched_holdings;
      return [...matched, ...unmatched];
    },

    aggregateHoldings(holdings) {
      const aggregated = {};

      holdings.forEach(holding => {
        // Create a unique key for each position (symbol + account + portfolio)
        // Use uploadedSymbol when available so proxied bonds with different CUSIPs are not merged
        const key = `${holding.uploadedSymbol || holding.activeSymbol}_${holding.account || 'no-account'}_${holding.portfolioName || 'no-portfolio'}`;

        if (!aggregated[key]) {
          // First time seeing this position - create aggregate entry
          aggregated[key] = {
            ...holding,
            id: `agg_${key}`,
            isAggregated: true,
            taxLots: [holding],
            shares: parseFloat(holding.shares) || 0,
            marketValue: parseFloat(holding.marketValue) || 0,
            totalCostBasis: parseFloat(holding.costBasis) || 0,
            // For aggregated positions, we'll show "Multiple" for purchase date if there are different dates
            originalPurchaseDate: holding.purchaseDate,
          };
        } else {
          // Add this holding as another tax lot
          aggregated[key].taxLots.push(holding);
          aggregated[key].shares += parseFloat(holding.shares) || 0;
          aggregated[key].marketValue += parseFloat(holding.marketValue) || 0;
          aggregated[key].totalCostBasis += parseFloat(holding.costBasis) || 0;

          // If purchase dates differ, mark as "Multiple"
          if (aggregated[key].originalPurchaseDate !== holding.purchaseDate) {
            aggregated[key].purchaseDate = 'Multiple dates';
          }
        }
      });

      // Convert back to array and add expanded tax lots if needed
      const result = [];
      Object.values(aggregated).forEach(aggHolding => {
        if (aggHolding.taxLots.length === 1) {
          // Single tax lot - just return the original holding
          result.push(aggHolding.taxLots[0]);
        } else {
          // Multiple tax lots - calculate weighted average cost basis and add the aggregated holding
          aggHolding.costBasis = aggHolding.totalCostBasis;
          result.push(aggHolding);

          // If this position is expanded, add the individual tax lots
          if (this.expandedPositions[aggHolding.id]) {
            aggHolding.taxLots.forEach((taxLot, index) => {
              result.push({
                ...taxLot,
                id: `${taxLot.id}_lot_${index}`,
                isChildTaxLot: true,
                parentId: aggHolding.id,
              });
            });
          }
        }
      });

      return result;
    },

    togglePositionExpansion(aggregatedId) {
      this.$set(this.expandedPositions, aggregatedId, !this.expandedPositions[aggregatedId]);
    },

    getRowClass(row) {
      if (row.isChildTaxLot) {
        return 'child-tax-lot';
      }
      return '';
    },

    // Add the autoGrow method which was referenced but not defined
    autoGrow(element) {
      if (!element) return;
      element.style.height = 'auto';
      element.style.height = element.scrollHeight + 'px';
    },
  },
};
</script>

<style scoped>
.summary {
  border-radius: 1em;
  background-color: var(--stk-background-alt);
  padding: 1em 1.5em;
}

.holdings-table {
  font-size: 0.75em;
}

.name-column {
  width: 200px;
  position: relative;
}

.action-buttons {
  position: absolute;
  top: 2px;
  right: 0;
  opacity: 0;
  transition: opacity 0.3s ease;
}

/* Instead of triggering on .name-column hover, trigger on the entire row */
.holdings-table tr:hover .action-buttons {
  opacity: 1;
}

.required {
  color: var(--stk-danger-60);
}

.action-buttons {
  position: absolute;
  top: 2px;
  right: 0;
  opacity: 0;
}
.italic {
  font-style: italic;
}
.custom-checkbox {
  /* Override the text color. Adjust the color to match your design. */
  color: var(--stk-text-color, #333);
}

.custom-checkbox .checkbox-icon {
  /* force a neutral border or background */
  border-color: #ccc;
  background-color: transparent;
}
.table-container {
  overflow: visible;
}
:deep(.o-table thead th) {
  position: sticky;
  top: 0;
  background-color: #fff;
  z-index: 10;
  border-bottom: 1px solid #ccc;
}
.flash-green {
  animation: flashGreen 1.3s ease-in-out;
}

@keyframes flashGreen {
  0% { background-color: #d4fce7; }
  100% { background-color: white; }
}
.disabled-input {
  background-color: #dcdcdc;
  color: #555;
  opacity: 1;
}
.required-cell {
  background-color: #fff5f5; /* a faint red/pink background */
  border: 2px solid #ffcccc;
}

/* Styles for tax lot aggregation */
.child-tax-lot {
  background-color: #f8f9fa;
  margin-left: 20px;
  border-left: 3px solid #dee2e6;
  padding-left: 8px;
}

.child-tax-lot .name-column {
  padding-left: 0;
}

:deep(.o-table tr.child-tax-lot) {
  background-color: #f8f9fa !important;
}

:deep(.o-table tr.child-tax-lot td) {
  border-top: 1px solid #dee2e6;
  font-size: 0.9em;
}
</style>
